import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMenusCabecera } from "../../../store/menusCabecera";
import { useUserStore } from "../../../store/userStore";
import { useUserCompanyName } from "../../../hooks/useUserCompanyName";
import PanelAnclado from "../menus/PanelAnclado";
import CabeceraDeApps from "../menus/CabeceraDeApps";
import RejillaDeApps, { type GrupoDeApps } from "../menus/RejillaDeApps";
import { filtrarApps } from "../menus/filtrarApps";
import { groupOf, groupText, splitForSidebar } from "../../../config/apps/useCaseGroups";
import { useCompanyUseCase } from "../../../features/useCase/useCompanyUseCase";
import MenuDeCuenta from "../menus/MenuDeCuenta";
import type { AppsSidebarProps, AppItem } from "./types";
import { filterCategoriesByEnvironment } from "./environmentVisibility.config";
import {
  trackSidebarOpen,
  trackSidebarClose,
  trackAppClick,
  trackAppNavigation,
  trackLockedAppAttempt,
  startSidebarSession,
  endSidebarSession,
  trackRepeatAppSelection,
  trackVisibleCategoriesCount,
  trackSidebarOpenFrequency,
  resetPageSessionOpenCount,
} from "./sidebarAnalytics";

/**
 * Los dos menús que cuelgan de la cabecera y necesitan datos del layout.
 *
 * ── Por qué sigue llamándose así y recibiendo lo mismo ──────────────────────
 * Lo montan 44 layouts con el mismo juego de props. Cambiar esa firma para
 * reordenar un menú serían 44 archivos tocados por una decisión de diseño, así
 * que la firma se queda y lo que cambia es lo que dibuja.
 *
 * ── Qué cambia ─────────────────────────────────────────────────────────────
 * Era un cajón de 340 px a altura completa, con velo y desenfoque, que metía
 * en una sola pila tres cosas distintas: los avisos (estado), el catálogo de
 * aplicaciones (destinos) y la cuenta (quién soy). Ahora son dos popovers
 * anclados a su propio botón —los avisos se los lleva la cabecera, que es
 * quien tiene la campana— y ninguno apaga la pantalla detrás.
 *
 * El acordeón por categoría también se fue: plegar un catálogo que cabe de una
 * vez cuesta un clic y guarda un estado que había que recordar entre
 * aperturas, a cambio de nada.
 */

export default function AppsSidebar({
  isOpen,
  onClose,
  categories,
  selectedAppId,
  onAppSelect,
  currentTheme = "system",
  onThemeChange,
  userInfo,
  onProfileClick,
  onLogout,
  isLoggingOut = false,
}: AppsSidebarProps) {
  const { t, i18n } = useTranslation("common");
  const user = useUserStore((state) => state.user);
  const empresa = useUserCompanyName();
  // Caso de uso de la compañía (CC-CONSOLE #1634): con uno guardado, las
  // categorías de su grupo van primero bajo un rótulo discreto y el resto
  // después; sin caso de uso (null) la rejilla es idéntica a la de siempre.
  const { useCase: casoDeUso } = useCompanyUseCase();

  const menuAbierto = useMenusCabecera((s) => s.abierto);
  const cerrarMenu = useMenusCabecera((s) => s.cerrar);

  const appsVistas = useRef<Set<string>>(new Set());

  const categoriasVisibles = useMemo(
    () => filterCategoriesByEnvironment(categories),
    [categories],
  );

  const [buscando, setBuscando] = useState(false);
  const [texto, setTexto] = useState("");
  const [seccion, setSeccion] = useState<string | null>(null);

  const categoriasFiltradas = useMemo(
    () => filtrarApps(categoriasVisibles, texto, seccion),
    [categoriasVisibles, texto, seccion],
  );
  const resultados = categoriasFiltradas.reduce((n, c) => n + c.apps.length, 0);

  // Bloques por caso de uso. Al buscar o filtrar por sección se vuelve a la
  // lista plana: el resultado de una búsqueda no necesita rótulos de grupo.
  const grupos = useMemo<GrupoDeApps[] | undefined>(() => {
    if (!casoDeUso || texto.trim() || seccion) return undefined;
    const { own, rest } = splitForSidebar(casoDeUso, categoriasVisibles);
    const grupo = groupOf(casoDeUso);
    return [
      { id: grupo.id, titulo: groupText(grupo.title, i18n.language), categorias: own },
      { id: "otras", titulo: t("appsSidebar.otherApps", "Otras aplicaciones"), categorias: rest },
    ];
  }, [casoDeUso, texto, seccion, categoriasVisibles, i18n.language, t]);

  const cerrarBusqueda = useCallback(() => {
    setBuscando(false);
    setTexto("");
    setSeccion(null);
  }, []);

  // `isOpen` sigue llegando de los layouts; el store lo abre desde la cabecera.
  // Cualquiera de los dos vale, y cerrar cierra los dos.
  const appsAbierto = isOpen || menuAbierto === "apps";
  const cuentaAbierto = menuAbierto === "cuenta";

  useEffect(() => {
    if (appsAbierto) {
      trackSidebarOpen(window.location.pathname);
      trackSidebarOpenFrequency();
      startSidebarSession();
      appsVistas.current = new Set();
      trackVisibleCategoriesCount(categoriasVisibles.length, categories.length);
    } else {
      endSidebarSession(
        appsVistas.current.size,
        0,
        categoriasVisibles.length,
        user?.role || undefined,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appsAbierto]);

  useEffect(() => {
    resetPageSessionOpenCount();
  }, []);

  const cerrarApps = useCallback(() => {
    trackSidebarClose("backdrop_click");
    cerrarBusqueda();
    cerrarMenu();
    onClose();
  }, [cerrarBusqueda, cerrarMenu, onClose]);

  const elegirApp = useCallback(
    (app: AppItem, categoriaId: string, categoriaTitulo: string) => {
      const estado = app.isComingSoon
        ? ("coming_soon" as const)
        : app.isLocked
          ? ("locked" as const)
          : app.disabled
            ? ("disabled" as const)
            : app.isBeta
              ? ("beta" as const)
              : ("active" as const);

      if (app.disabled || app.isComingSoon || app.isLocked) {
        trackLockedAppAttempt(
          app.id,
          app.name,
          categoriaId,
          app.isComingSoon ? "coming_soon" : app.isLocked ? "locked" : "disabled",
        );
        if (app.disabled || app.isComingSoon) return;
      }

      trackAppClick(
        app.id,
        app.name,
        categoriaId,
        categoriaTitulo,
        app.route || app.externalUrl || "",
        estado,
      );
      if (selectedAppId === app.id) trackRepeatAppSelection(app.id, app.name, categoriaId);
      if (selectedAppId && selectedAppId !== app.id) {
        trackAppNavigation(selectedAppId, app.id, categoriaId, app.route || app.externalUrl || "");
      }

      appsVistas.current.add(app.id);
      cerrarApps();
      onAppSelect?.(app);
    },
    [onAppSelect, selectedAppId, cerrarApps],
  );

  return (
    <>
      {appsAbierto && (
        <PanelAnclado
          abierto
          onCerrar={cerrarApps}
          anclaSelector='[data-ancla-menu="apps"]'
          etiqueta={t("appsSidebar.title")}
          ancho={340}
        >
          <CabeceraDeApps
            abierto={buscando}
            onAbrir={() => setBuscando(true)}
            onCerrar={cerrarBusqueda}
            texto={texto}
            onTexto={setTexto}
            categorias={categoriasVisibles}
            seccion={seccion}
            onSeccion={setSeccion}
            resultados={resultados}
          />
          <div className="hud-filo h-px shrink-0" aria-hidden="true" />
          <div className="min-h-0 flex-1 overflow-y-auto">
            {resultados === 0 && texto ? (
              <p className="px-4 py-10 text-center text-xs text-content-muted">
                {t("appsSidebar.noResults", "Ninguna aplicación coincide")}
              </p>
            ) : (
              <RejillaDeApps
                categorias={categoriasFiltradas}
                grupos={grupos}
                appSeleccionada={selectedAppId}
                onElegir={elegirApp}
              />
            )}
          </div>
        </PanelAnclado>
      )}

      {cuentaAbierto && (
        <PanelAnclado
          abierto
          onCerrar={cerrarMenu}
          anclaSelector='[data-ancla-menu="cuenta"]'
          etiqueta={t("appsSidebar.account", "Cuenta")}
          ancho={288}
        >
          <MenuDeCuenta
            usuario={userInfo}
            empresa={empresa ?? undefined}
            rol={localStorage.getItem("userRole") || undefined}
            tema={currentTheme}
            onTema={onThemeChange}
            onPerfil={() => {
              cerrarMenu();
              onProfileClick?.();
            }}
            onCerrarSesion={onLogout}
            cerrandoSesion={isLoggingOut}
          />
        </PanelAnclado>
      )}
    </>
  );
}
